import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Card,
  CardContent,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
  Rating,
  useTheme,
  useMediaQuery,
  Grid,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  DirectionsCar as CarIcon,
  CalendarMonth as CalendarIcon,
  WhatsApp as WhatsAppIcon,
  CheckCircle as ProgressIcon,
  Receipt as ReceiptIcon,
  Star as StarIcon,
  Sync as SyncIcon,
  Map as MapIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  Business as BuildingIcon,
  Security as SecurityIcon,
  Psychology as BrainIcon,
  Share as ShareIcon,
  CreditCard as PaymentIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { REGION_GROUPS } from './SearchInstructors';

const LandingPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [dynamicTestimonials, setDynamicTestimonials] = React.useState<any[]>([]);

  // Search form states (Gearbox, License Type, City, District)
  const [transmission, setTransmission] = React.useState('all');
  const [licenseType, setLicenseType] = React.useState('all');
  const [selectedCity, setSelectedCity] = React.useState('');
  const [selectedDistrict, setSelectedDistrict] = React.useState('');

  // Testimonial selection state
  const [activeTestiIndex, setActiveTestiIndex] = React.useState(1);

  React.useEffect(() => {
    const fetchLandingTestimonials = async () => {
      try {
        const response = await api.getLandingTestimonials();
        if (response.data && response.data.length > 0) {
          setDynamicTestimonials(response.data);
        }
      } catch (err) {
        console.error('Failed to load landing page testimonials:', err);
      }
    };
    fetchLandingTestimonials();
  }, []);

  React.useEffect(() => {
    document.title = "adiLessons - UK Driving Instructor Scheduling & Syllabus Tracking App";

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Manage your driving school like a modern business. Automated WhatsApp lesson reminders, UK DVSA syllabus tracking, calendar bookings, and online payments for ADI driving instructors.');

    const ogTags = [
      { property: 'og:title', content: 'adiLessons - Driving Instructor Scheduler' },
      { property: 'og:description', content: 'Automated WhatsApp reminders, calendar bookings, and DVSA syllabus tracking.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: window.location.href }
    ];
    
    ogTags.forEach(tag => {
      let element = document.querySelector(`meta[property="${tag.property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', tag.property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });

    const schema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "adiLessons",
      "operatingSystem": "All",
      "applicationCategory": "BusinessApplication",
      "offers": {
        "@type": "AggregateOffer",
        "priceCurrency": "GBP",
        "lowPrice": "10.00",
        "highPrice": "15.00",
        "price": "10.00"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "120"
      }
    };
    
    let schemaScript = document.getElementById('jsonld-schema') as HTMLScriptElement;
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = 'jsonld-schema';
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }
    schemaScript.text = JSON.stringify(schema);

    return () => {
      const script = document.getElementById('jsonld-schema');
      if (script) script.remove();
    };
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let query = `/search`;
    const params: string[] = [];
    if (transmission !== 'all') {
      params.push(`transmission=${encodeURIComponent(transmission)}`);
    }
    if (licenseType !== 'all') {
      params.push(`licenseType=${encodeURIComponent(licenseType)}`);
    }
    if (selectedCity) {
      params.push(`city=${encodeURIComponent(selectedCity)}`);
    }
    if (selectedDistrict) {
      params.push(`district=${encodeURIComponent(selectedDistrict)}`);
    }
    if (params.length > 0) {
      query += `?${params.join('&')}`;
    }
    navigate(query);
  };

  const navItems = [
    { label: 'Features', path: '/features' },
    { label: 'Pricing', path: '/pricing' },
    { label: 'About Us', path: '/about' },
    { label: 'Security', path: '/security' }
  ];

  const features = [
    {
      icon: <CalendarIcon sx={{ fontSize: 32, color: '#1967D2' }} />,
      title: '24/7 Autopilot Scheduling',
      description: 'Publish your open slots and let students book, reschedule, or cancel on their own 24/7. Say goodbye to manual scheduling and double-bookings.'
    },
    {
      icon: <SyncIcon sx={{ fontSize: 32, color: '#1967D2' }} />,
      title: 'Google Calendar Sync',
      description: 'Keep your personal and professional calendars in perfect sync. Instructors can connect their personal Google accounts to synchronize all lesson slots and bookings automatically.'
    },
    {
      icon: <ProgressIcon sx={{ fontSize: 32, color: '#1967D2' }} />,
      title: 'Interactive DVSA Progress',
      description: 'Track training progress in real-time against official DVSA criteria. Visual completion meters show exactly when students are test-ready.'
    },
    {
      icon: <WhatsAppIcon sx={{ fontSize: 32, color: '#1967D2' }} />,
      title: 'Instant WhatsApp Notifications',
      description: 'Reduce missed lessons with automatic WhatsApp confirmations and reminders. Automatically dispatch instant booking confirmations, change alerts, and timely lesson reminders straight from your own phone number.'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 32, color: '#1967D2' }} />,
      title: 'Cancellation Policy Protection',
      description: 'Protect your income. Enforce a 24-hour late cancellation threshold. Late cancellations automatically generate a pending charge equal to the full lesson fee on student profiles.'
    },
    {
      icon: <ReceiptIcon sx={{ fontSize: 32, color: '#1967D2' }} />,
      title: 'HMRC-Ready Expenses & Tax',
      description: 'Track mileage and fuel fill-ups. Automatically calculates standard UK HMRC mileage allowances (£0.45/mile) vs actual vehicle costs to optimize your yearly Self-Assessment tax returns.'
    },
    {
      icon: <MapIcon sx={{ fontSize: 32, color: '#1967D2' }} />,
      title: 'Google Maps Test Routes',
      description: 'Acquire local test centre routes. Explore interactive visual maps defining exact driving test centre tracks, complete with speed limits and safety warnings pinned by expert instructors.'
    },
    {
      icon: <BrainIcon sx={{ fontSize: 32, color: '#1967D2' }} />,
      title: 'AI Study Buddy Helper',
      description: 'Supercharge your theory revision. Our AI engine analyzes mock theory test history, groups success rates by DVSA category, and recommends targeted study topics and practice exams.'
    },
    {
      icon: <ShareIcon sx={{ fontSize: 32, color: '#1967D2' }} />,
      title: 'Referral Reward Programs',
      description: 'Grow your business organically. Instructors refer peers to earn free subscription months, and students refer friends to earn free lesson credits when booking lessons.'
    },
    {
      icon: <PaymentIcon sx={{ fontSize: 32, color: '#1967D2' }} />,
      title: 'PayPal & Quote Enquiries',
      description: 'Accept credit card payments online or sell prepaid lesson packages. Students can submit detailed gearbox, postcode, and experience quotes directly to their preferred local ADIs.'
    },
    {
      icon: <SchoolIcon sx={{ fontSize: 32, color: '#1967D2' }} />,
      title: 'Theory & Hazard Pass Kit',
      description: 'Give students the ultimate pass-guarantee kit. Offer official OGL-compliant mock theory tests and hazard perception video clips right inside their app. Elevate your school\'s reputation.'
    },
    {
      icon: <StarIcon sx={{ fontSize: 32, color: '#1967D2' }} />,
      title: 'Verified Dynamic Testimonials',
      description: 'Build immediate trust. Show off verified platform feedback. Instructors and students submit star reviews, which appear directly on the landing page after admin approval.'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Register & Find Instructor',
      description: 'Create an account and filter local instructors by gearbox, prices, postcodes, and star reviews.'
    },
    {
      number: '2',
      title: 'Schedule Lessons 24/7',
      description: 'Book slots directly on the calendar. Receive instant WhatsApp confirmations and automated reminders.'
    },
    {
      number: '3',
      title: 'Track Syllabus & Pass',
      description: 'Monitor your driving skills against DVSA guidelines and check your test readiness completion score.'
    }
  ];

  const pricingPlans = [
    {
      title: 'Base Plan',
      price: '£10.00',
      period: 'per month',
      description: 'Essential tools to digitalise your business, schedule classes, and track student success.',
      features: [
        'Unlimited Students & Bookings',
        '24/7 Smart Availability Calendar',
        'Interactive DVSA Progress Tracker',
        'Business Expense Logger & Reports',
        'Mock Theory & Hazard Perception',
        'Secure PayPal Payment Gateway',
        'WhatsApp Module (Locked)'
      ],
      ctaText: 'Get Started',
      popular: false,
      note: 'Or £99/year (Save 17%)'
    },
    {
      title: 'Premium Plan',
      price: '£15.00',
      period: 'per month',
      description: 'The complete business operating system with automated WhatsApp reminders to reduce missed lessons.',
      features: [
        'Everything in the Base Plan',
        'Automated WhatsApp Alerts',
        'Instant WhatsApp Lesson Reminders',
        'WhapiCell QR Device Integration',
        'Direct Student Messaging Service',
        'Priority 24/7 Customer Support'
      ],
      ctaText: 'Unlock Full Power',
      popular: true,
      note: 'Or £149/year (Save 17%)'
    },
    {
      title: 'Custom Web Plan',
      price: '£35.00',
      period: 'per month',
      description: 'Establish a professional web presence with your own personalized subdomain, custom themes, and automated lead capture.',
      features: [
        'Everything in the Premium Plan',
        'Personalized Subdomain (name.adilesson.co.uk)',
        'Custom Domains Integration (e.g. www.yourname.co.uk)',
        'Beautiful, High-Converting Website Themes',
        '1-Click "Convert Lead to Pupil" Booking Requests',
        'P2P DVSA Test Slot Exchange Network Access',
        'Receipt Scanner OCR (Auto-fill Expenses)'
      ],
      ctaText: 'Build Your Brand',
      popular: false,
      note: 'Or £349/year (Save 17%)'
    }
  ];

  const featuredInstructors = [
    {
      name: 'Sarah Jenkins',
      role: 'Grade A ADI Instructor',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&auto=format&fit=crop&q=80',
      rating: 5,
      price: '£38',
      gears: 'Automatic',
      location: 'Enfield, London',
      experience: '8 years experience'
    },
    {
      name: 'David Miller',
      role: 'Senior Lead ADI',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&auto=format&fit=crop&q=80',
      rating: 5,
      price: '£37',
      gears: 'Manual',
      location: 'Wood Green, London',
      experience: '12 years experience'
    },
    {
      name: 'James O\'Connor',
      role: 'Fleet & ADI Trainer',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&auto=format&fit=crop&q=80',
      rating: 5,
      price: '£40',
      gears: 'Manual & Automatic',
      location: 'Tottenham, London',
      experience: '15 years experience'
    },
    {
      name: 'Emma Watson',
      role: 'Mock Exam Specialist',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=120&auto=format&fit=crop&q=80',
      rating: 5,
      price: '£39',
      gears: 'Manual',
      location: 'Hendon, London',
      experience: '6 years experience'
    }
  ];

  const testimonialAvatars = [
    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=120&auto=format&fit=crop&q=80',
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=120&auto=format&fit=crop&q=80'
  ];

  const localTestCentres = [
    { name: 'Wood Green Test Centre', location: 'London, UK', instructors: '15 Active ADIs', rate: 'From £35/hr' },
    { name: 'Enfield Test Centre', location: 'London, UK', instructors: '22 Active ADIs', rate: 'From £36/hr' },
    { name: 'Tottenham Test Centre', location: 'London, UK', instructors: '18 Active ADIs', rate: 'From £35/hr' },
    { name: 'Hendon Test Centre', location: 'London, UK', instructors: '14 Active ADIs', rate: 'From £37/hr' }
  ];

  const blogArticles = [
    {
      category: 'Practical Test',
      date: 'April 06 2026',
      author: 'By Sarah Jenkins',
      title: 'Top 10 Hardest Roundabouts in North London & How to Navigate Them',
      image: 'https://images.unsplash.com/photo-1449034446853-66c86144b0ad?w=600&auto=format&fit=crop&q=80'
    },
    {
      category: 'Theory Revision',
      date: 'March 28 2026',
      author: 'By David Miller',
      title: 'How our AI Study Buddy Helps You Pass Your Theory Exam First Time',
      image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=600&auto=format&fit=crop&q=80'
    },
    {
      category: 'ADI Business',
      date: 'March 15 2026',
      author: 'By James O\'Connor',
      title: 'Understanding HMRC Standard Mileage Allowances for Driving Instructors',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&auto=format&fit=crop&q=80'
    }
  ];

  const displayTestimonials = dynamicTestimonials.map((dt, i) => ({
    id: i + 1,
    name: dt.authorName,
    role: dt.role,
    avatar: testimonialAvatars[i % testimonialAvatars.length],
    rating: dt.rating,
    comment: dt.content
  }));

  const currentTestimonial = displayTestimonials.find(t => t.id === activeTestiIndex) || displayTestimonials[0] || null;

  const getDashboardPath = () => {
    if (!user) return '/login';
    if (user.role === 'Admin') return '/admin/dashboard';
    if (user.role === 'Instructor') return '/instructor/dashboard';
    return '/student/dashboard';
  };

  const selectedCityOutcodes = REGION_GROUPS.find(r => r.name === selectedCity)?.outcodes || [];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#ffffff', color: '#2b3154' }}>
      {/* Dynamic Glows and Styles */}
      <style>
        {`
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0px); }
          }
          html {
            scroll-behavior: smooth;
          }
        `}
      </style>

      {/* Sticky Main Header (header-style-four -type-16 style) */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          background: 'rgba(255, 255, 255, 0.95)', 
          backdropFilter: 'blur(12px)', 
          borderBottom: '1px solid #ecedf2', 
          color: '#2b3154', 
          zIndex: 1100 
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', height: 80 }}>
            {/* Logo */}
            <Box display="flex" alignItems="center" gap={1} onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
              <CarIcon sx={{ color: '#1967D2', fontSize: 28 }} />
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#2b3154', 
                  letterSpacing: '-0.5px',
                  fontFamily: '"Outfit", "Inter", sans-serif'
                }}
              >
                adi<span style={{ color: '#E53935', fontWeight: '900' }}>L</span>essons
              </Typography>
            </Box>

            {/* Nav Menu Items */}
            {!isMobile && (
              <Box display="flex" gap={4} alignItems="center">
                {navItems.map((item) => (
                  <Typography
                    key={item.label}
                    onClick={() => navigate(item.path)}
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'none',
                      color: '#2b3154',
                      fontWeight: '500',
                      fontSize: '15px',
                      transition: 'color 0.2s',
                      '&:hover': { color: '#1967D2' }
                    }}
                  >
                    {item.label}
                  </Typography>
                ))}
                <Typography
                  onClick={() => navigate('/search')}
                  sx={{
                    cursor: 'pointer',
                    color: '#1967D2',
                    fontWeight: '600',
                    fontSize: '15px',
                    transition: 'color 0.2s',
                    '&:hover': { color: '#1a237e' }
                  }}
                >
                  Find an Instructor
                </Typography>
              </Box>
            )}

            {/* Login/CTA Box */}
            <Box display="flex" gap={2} alignItems="center">
              {isAuthenticated ? (
                <Button
                  variant="contained"
                  onClick={() => navigate(getDashboardPath())}
                  sx={{ 
                    borderRadius: '12px', 
                    px: 3.5, 
                    py: 1.5,
                    fontWeight: 'bold', 
                    textTransform: 'none', 
                    bgcolor: '#1967D2',
                    fontSize: '15px',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#1556b3' }
                  }}
                >
                  Go to Dashboard
                </Button>
              ) : (
                <>
                  {!isMobile && (
                    <Button
                      variant="text"
                      onClick={() => navigate('/login')}
                      sx={{ color: '#2b3154', fontWeight: 'bold', textTransform: 'none', fontSize: '15px' }}
                    >
                      Login / Register
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={() => navigate('/register')}
                    sx={{ 
                      borderRadius: '12px', 
                      px: 3.5, 
                      py: 1.5,
                      fontWeight: 'bold', 
                      textTransform: 'none', 
                      bgcolor: '#1967D2',
                      fontSize: '15px',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#1556b3' }
                    }}
                  >
                    Get Started
                  </Button>
                </>
              )}

              {isMobile && (
                <IconButton onClick={handleDrawerToggle} edge="end" sx={{ color: '#2b3154' }}>
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { width: 270 } }}
      >
        <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold" color="primary">Menu</Typography>
          <IconButton onClick={handleDrawerToggle}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List>
          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <Typography
                onClick={() => {
                  handleDrawerToggle();
                  navigate(item.path);
                }}
                sx={{
                  display: 'block',
                  width: '100%',
                  py: 1.5,
                  px: 3,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: '#2b3154',
                  fontWeight: '500',
                  '&:hover': { backgroundColor: '#f5f7f8', color: '#1967D2' }
                }}
              >
                {item.label}
              </Typography>
            </ListItem>
          ))}
          <ListItem disablePadding>
            <Typography
              onClick={() => { handleDrawerToggle(); navigate('/search'); }}
              sx={{
                cursor: 'pointer',
                display: 'block',
                width: '100%',
                py: 1.5,
                px: 3,
                textDecoration: 'none',
                color: '#1967D2',
                fontWeight: '600',
                '&:hover': { backgroundColor: '#f5f7f8', color: '#1556b3' }
              }}
            >
              Find an Instructor
            </Typography>
          </ListItem>
        </List>
        {!isAuthenticated && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" flexDirection="column" gap={1.5} sx={{ p: 2 }}>
              <Button fullWidth variant="outlined" onClick={() => { handleDrawerToggle(); navigate('/login'); }} sx={{ borderRadius: '12px' }}>
                Sign In
              </Button>
              <Button fullWidth variant="contained" onClick={() => { handleDrawerToggle(); navigate('/register'); }} sx={{ borderRadius: '12px', bgcolor: '#1967D2' }}>
                Get Started
              </Button>
            </Box>
          </>
        )}
      </Drawer>

      {/* Hero Banner Section (banner-section-three at-home19 style) */}
      <Box 
        sx={{ 
          bgcolor: '#0f162a', 
          color: '#ffffff', 
          py: { xs: 8, md: 14 }, 
          position: 'relative', 
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: "url('/driving_background.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(5px) brightness(0.32)',
            transform: 'scale(1.05)',
            zIndex: 1,
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center', maxWidth: '880px', mx: 'auto', mb: 5 }}>
            {/* Accent Tag */}
            <Box 
              display="inline-flex" 
              alignItems="center" 
              gap={1} 
              sx={{ 
                px: 2, 
                py: 0.8, 
                borderRadius: '30px', 
                bgcolor: 'rgba(0, 255, 132, 0.1)', 
                color: '#00FF84', 
                mb: 3,
                fontWeight: 'bold',
                fontSize: '13px',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
            >
              <CarIcon sx={{ fontSize: 16 }} />
              Over 500+ Local ADI Instructors
            </Box>

            {/* Main Heading */}
            <Typography 
              variant="h2" 
              component="h1" 
              sx={{ 
                fontWeight: 800, 
                lineHeight: 1.2, 
                mb: 3, 
                fontSize: { xs: '2.2rem', sm: '3rem', md: '3.8rem' },
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                fontFamily: '"Outfit", "Inter", sans-serif'
              }}
            >
              Run your driving school <br />
              without <span style={{ color: '#00FF84' }}>diary chaos</span>.
            </Typography>

            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255,255,255,0.85)', 
                fontWeight: 'normal', 
                lineHeight: 1.6, 
                mb: 4,
                fontSize: '18px',
                maxWidth: '750px',
                mx: 'auto',
                textShadow: '0 1px 4px rgba(0,0,0,0.3)'
              }}
            >
              Schedule lessons, track pupil progress against the DVSA syllabus, collect payments and send automated WhatsApp reminders from one platform built for UK approved driving instructors (ADIs) and driving schools.
            </Typography>

            <Box display="flex" justifyContent="center" gap={2} sx={{ mb: 6, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/register')}
                sx={{ 
                  borderRadius: '12px', 
                  px: 4, 
                  py: 2,
                  fontWeight: 'bold', 
                  textTransform: 'none', 
                  bgcolor: '#00FF84',
                  color: '#0f162a',
                  fontSize: '16px',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#00e074' }
                }}
              >
                Start Your 14-Day Free Trial
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/features')}
                sx={{ 
                  borderRadius: '12px', 
                  px: 4, 
                  py: 2,
                  fontWeight: 'bold', 
                  textTransform: 'none', 
                  borderColor: '#ffffff',
                  color: '#ffffff',
                  fontSize: '16px',
                  borderWidth: '2px',
                  '&:hover': { borderColor: '#00FF84', color: '#00FF84', borderWidth: '2px' }
                }}
              >
                See how it works
              </Button>
            </Box>

            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: 'rgba(255,255,255,0.7)', 
                fontWeight: 'bold', 
                mb: 2,
                fontSize: '13px',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
            >
              Are you a pupil? Find local instructors & book lessons
            </Typography>
          </Box>

          {/* Instructor Search Form */}
          <Paper 
            component="form" 
            onSubmit={handleSearchSubmit}
            elevation={0}
            sx={{ 
              p: '10px', 
              borderRadius: '16px', 
              bgcolor: '#ffffff', 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1.2fr 1.2fr 1.2fr 1.2fr 1fr' },
              gap: 1.5,
              mb: 8,
              maxWidth: '1000px',
              mx: 'auto',
              boxShadow: '0px 15px 35px rgba(0,0,0,0.35)'
            }}
          >
            {/* Vites (Gearbox) select */}
            <Box sx={{ px: 2, py: 1, borderRight: { md: '1px solid #ecedf2' } }}>
              <Typography variant="caption" sx={{ color: '#7a829a', display: 'block', fontWeight: '500', mb: 0.5 }}>Transmission</Typography>
              <Select
                value={transmission}
                onChange={(e) => setTransmission(e.target.value)}
                variant="standard"
                disableUnderline
                fullWidth
                sx={{ color: '#2b3154', fontWeight: '500', fontSize: '14px' }}
              >
                <MenuItem value="all">Any Gearbox</MenuItem>
                <MenuItem value="Manual">Manual</MenuItem>
                <MenuItem value="Automatic">Automatic</MenuItem>
              </Select>
            </Box>

            {/* Ehliyet Tipi (License Type) select */}
            <Box sx={{ px: 2, py: 1, borderRight: { md: '1px solid #ecedf2' } }}>
              <Typography variant="caption" sx={{ color: '#7a829a', display: 'block', fontWeight: '500', mb: 0.5 }}>License Type</Typography>
              <Select
                value={licenseType}
                onChange={(e) => setLicenseType(e.target.value)}
                variant="standard"
                disableUnderline
                fullWidth
                sx={{ color: '#2b3154', fontWeight: '500', fontSize: '14px' }}
              >
                <MenuItem value="all">Any License</MenuItem>
                <MenuItem value="B">Category B (Car)</MenuItem>
                <MenuItem value="A">Category A (Motorcycle)</MenuItem>
                <MenuItem value="C">Category C (Lorry)</MenuItem>
                <MenuItem value="D">Category D (Bus)</MenuItem>
                <MenuItem value="BE">Category B+E (Trailer)</MenuItem>
              </Select>
            </Box>

            {/* İl (City) select */}
            <Box sx={{ px: 2, py: 1, borderRight: { md: '1px solid #ecedf2' } }}>
              <Typography variant="caption" sx={{ color: '#7a829a', display: 'block', fontWeight: '500', mb: 0.5 }}>City</Typography>
              <Select
                value={selectedCity}
                onChange={(e) => {
                  setSelectedCity(e.target.value);
                  setSelectedDistrict('');
                }}
                variant="standard"
                disableUnderline
                fullWidth
                displayEmpty
                sx={{ color: '#2b3154', fontWeight: '500', fontSize: '14px' }}
              >
                <MenuItem value="" disabled>Select City</MenuItem>
                {REGION_GROUPS.map((city) => (
                  <MenuItem key={city.name} value={city.name}>{city.name}</MenuItem>
                ))}
              </Select>
            </Box>

            {/* İlçe (District) select */}
            <Box sx={{ px: 2, py: 1, borderRight: { md: '1px solid #ecedf2' } }}>
              <Typography variant="caption" sx={{ color: '#7a829a', display: 'block', fontWeight: '500', mb: 0.5 }}>District</Typography>
              <Select
                value={selectedDistrict}
                onChange={(e) => setSelectedDistrict(e.target.value)}
                variant="standard"
                disableUnderline
                fullWidth
                displayEmpty
                disabled={!selectedCity}
                sx={{ color: '#2b3154', fontWeight: '500', fontSize: '14px' }}
              >
                <MenuItem value="" disabled>Select District</MenuItem>
                {selectedCityOutcodes.map((dist) => (
                  <MenuItem key={dist.code} value={dist.code}>{dist.area.split(',')[0]}</MenuItem>
                ))}
              </Select>
            </Box>

            {/* Search Button */}
            <Box display="flex" alignItems="center" justifyContent="center" sx={{ p: 0.5 }}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ 
                  bgcolor: '#1967D2', 
                  color: '#ffffff', 
                  py: 2.2,
                  borderRadius: '12px',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  fontSize: '15px',
                  boxShadow: 'none',
                  height: '100%',
                  '&:hover': { bgcolor: '#1556b3' }
                }}
              >
                Find ADIs
              </Button>
            </Box>
          </Paper>

          {/* Fun Fact Section */}
          <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: '800px', mx: 'auto', mb: 8 }}>
            <Grid size={{ xs: 6, sm: 3 }} sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#00FF84' }}>10k+</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Active Students</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }} sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ffffff' }}>500+</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>ADI Instructors</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }} sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ffffff' }}>250k+</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Lessons Booked</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }} sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ffffff' }}>99.8%</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Alerts Delivered</Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Platform Features Grid (Job Categories Style) */}
      <Box id="features" sx={{ pt: { xs: 10, md: 14 }, pb: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyItems="center" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 6 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: '#2b3154' }}>Professional ADI Diary & Driving School Software</Typography>
              <Typography variant="body1" sx={{ color: '#7a829a' }}>Everything you need to automate lesson scheduling, DVSA progress tracking, and driving school finance.</Typography>
            </Box>
            <Button 
              onClick={() => navigate('/register')}
              endIcon={<ArrowForwardIcon />} 
              sx={{ color: '#1967D2', fontWeight: 'bold', mt: { xs: 2, sm: 0 }, textTransform: 'none', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
            >
              See all features
            </Button>
          </Box>

          <Grid container spacing={3.5}>
            {features.map((feature, idx) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: '18px',
                    p: 3,
                    bgcolor: '#F0F5F7',
                    boxShadow: '0px 6px 15px rgba(64, 79, 104, 0.03)',
                    transition: 'all 0.3s ease-in-out',
                    cursor: 'default',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      bgcolor: '#1967D2',
                      color: '#ffffff',
                      transform: 'translateY(-6px)',
                      boxShadow: '0px 12px 25px rgba(25, 103, 210, 0.15)',
                      '& .icon-box': {
                        bgcolor: '#ffffff',
                        color: '#1967D2'
                      },
                      '& .title-text': {
                        color: '#ffffff'
                      },
                      '& .desc-text': {
                        color: 'rgba(255,255,255,0.8)'
                      }
                    }
                  }}
                >
                  <Box 
                    className="icon-box"
                    sx={{ 
                      width: 72, 
                      height: 72, 
                      borderRadius: '16px', 
                      bgcolor: '#ffffff', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      mb: 3.5,
                      border: '1px solid #ecedf2',
                      transition: 'all 0.3s ease-in-out'
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography className="title-text" variant="h6" sx={{ fontWeight: 'bold', mb: 1.5, color: '#2b3154' }}>
                    {feature.title}
                  </Typography>
                  <Typography className="desc-text" variant="body2" sx={{ color: '#7a829a', lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Featured Instructors Grid (Featured Jobs style) */}
      <Box sx={{ py: { xs: 10, md: 14 }, bgcolor: '#F0F5F7' }}>
        <Container maxWidth="lg">
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyItems="center" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 6 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: '#2b3154' }}>Featured Instructors</Typography>
              <Typography variant="body1" sx={{ color: '#7a829a' }}>Compare credentials, ratings, gearbox options, and hourly prices.</Typography>
            </Box>
            <Button 
              onClick={() => navigate('/search')}
              endIcon={<ArrowForwardIcon />} 
              sx={{ color: '#1967D2', fontWeight: 'bold', mt: { xs: 2, sm: 0 }, textTransform: 'none', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
            >
              View All Instructors
            </Button>
          </Box>

          <Grid container spacing={3.5}>
            {featuredInstructors.map((inst, idx) => (
              <Grid size={{ xs: 12, md: 6 }} key={idx}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3.5,
                    borderRadius: '20px',
                    bgcolor: '#ffffff',
                    border: '1px solid #ecedf2',
                    transition: 'all 0.3s',
                    position: 'relative',
                    '&:hover': {
                      borderColor: '#1967D2',
                      boxShadow: '0px 10px 25px rgba(64, 79, 104, 0.05)',
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" gap={3}>
                    <Avatar 
                      src={inst.avatar} 
                      alt={inst.name} 
                      sx={{ width: 80, height: 80, border: '3px solid #F0F5F7' }} 
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2b3154', mb: 0.5 }}>{inst.name}</Typography>
                      <Typography variant="body2" sx={{ color: '#1967D2', fontWeight: '500', mb: 1 }}>{inst.role}</Typography>
                      
                      <Box display="flex" flexWrap="wrap" gap={2} sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ color: '#7a829a', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CarIcon sx={{ fontSize: 14 }} /> {inst.gears}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#7a829a', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MapIcon sx={{ fontSize: 14 }} /> {inst.location}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#7a829a', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ProgressIcon sx={{ fontSize: 14 }} /> {inst.experience}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Price Tag & Rating */}
                    <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between', height: '100%' }}>
                      <Typography variant="h5" sx={{ fontWeight: '800', color: '#2b3154' }}>
                        {inst.price}<span style={{ fontSize: '13px', fontWeight: 'normal', color: '#7a829a' }}>/hr</span>
                      </Typography>
                      <Box display="flex" alignItems="center" sx={{ mt: 1 }}>
                        <StarIcon sx={{ color: '#ffc107', fontSize: 16 }} />
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2b3154', ml: 0.5 }}>{inst.rating}.0</Typography>
                      </Box>
                      <Button
                        variant="contained"
                        onClick={() => navigate(`/search`)}
                        size="small"
                        sx={{
                          mt: 2,
                          bgcolor: 'rgba(25, 103, 210, 0.08)',
                          color: '#1967D2',
                          fontWeight: 'bold',
                          borderRadius: '8px',
                          textTransform: 'none',
                          boxShadow: 'none',
                          '&:hover': {
                            bgcolor: '#1967D2',
                            color: '#ffffff'
                          }
                        }}
                      >
                        Book Lesson
                      </Button>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Onboarding Selling Points (CTA 2 Section Style) */}
      <Box 
        sx={{ 
          bgcolor: '#0f162a', 
          color: '#ffffff', 
          py: { xs: 10, md: 14 },
          backgroundImage: 'radial-gradient(circle at 10% 80%, rgba(0, 255, 132, 0.08) 0%, rgba(0, 0, 0, 0) 50%)'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Left Image Mockup Column */}
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'block' }}>
              <Box 
                sx={{
                  border: '10px solid rgba(255,255,255,0.05)',
                  borderRadius: '24px',
                  boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.3)',
                  overflow: 'hidden'
                }}
              >
                <Box 
                  component="img"
                  src="/drvman_hero.png"
                  alt="adiLessons app dashboard mockup"
                  sx={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </Box>
            </Grid>

            {/* Right Content Column */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ pl: { md: 4 } }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 3.5, lineHeight: 1.25 }}>
                  Get Your UK Practical Driving Licence Quickly
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 4, lineHeight: 1.65 }}>
                  Search all registered local instructors on the web. Get instant quotes based on your gearbox preferences and postcode area. Track your practical training syllabus step-by-step and schedule lessons dynamically.
                </Typography>

                <List sx={{ mb: 5 }}>
                  {[
                    '24/7 smart availability slots calendar with instant booking approvals.',
                    'Automated lesson reminders via WhatsApp to prevent late cancellations.',
                    'Official UK DVSA syllabus tracking to visual completion meters.',
                    'HMRC-ready vehicle mileage expense calculations & tax optimizer.'
                  ].map((text, index) => (
                    <ListItem key={index} disableGutters sx={{ py: 1.2, alignItems: 'flex-start' }}>
                      <Box 
                        sx={{ 
                          width: 22, 
                          height: 22, 
                          borderRadius: '50%', 
                          bgcolor: 'rgba(0, 255, 132, 0.15)', 
                          color: '#00FF84', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          mr: 2,
                          mt: 0.5,
                          flexShrink: 0
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 13, fontWeight: 'bold' }} />
                      </Box>
                      <ListItemText primary={text} primaryTypographyProps={{ sx: { fontSize: '15px', color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 } }} />
                    </ListItem>
                  ))}
                </List>

                <Button
                  variant="contained"
                  onClick={() => navigate('/register')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: '#00FF84',
                    color: '#0f162a',
                    fontWeight: 'bold',
                    px: 4.5,
                    py: 1.8,
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontSize: '15px',
                    boxShadow: '0px 5px 20px rgba(0, 255, 132, 0.3)',
                    '&:hover': {
                      bgcolor: '#00cc6a',
                      boxShadow: '0px 5px 20px rgba(0, 255, 132, 0.5)'
                    }
                  }}
                >
                  Get Started Now
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 3 Easy Steps Process Section */}
      <Box id="how-it-works" sx={{ py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 9, maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2b3154', mb: 1.5 }}>
              Start Booking Driving Lessons in 3 Easy Steps
            </Typography>
            <Typography variant="body1" sx={{ color: '#7a829a' }}>
              We\'ve streamlined the entire ADI scheduling, pupil booking, and DVSA skill tracking workflow.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {steps.map((step, idx) => (
              <Grid size={{ xs: 12, md: 4 }} key={idx}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4.5,
                    height: '100%',
                    borderRadius: '20px',
                    bgcolor: '#ffffff',
                    border: '1px solid #ecedf2',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: '#1967D2',
                      boxShadow: '0px 10px 30px rgba(64, 79, 104, 0.05)'
                    }
                  }}
                >
                  {/* Step Number Circle */}
                  <Box 
                    sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%', 
                      bgcolor: '#F0F5F7', 
                      color: '#1967D2', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '18px',
                      mb: 3
                    }}
                  >
                    {step.number}
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2b3154', mb: 2 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#7a829a', lineHeight: 1.65 }}>
                    {step.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box id="pricing" sx={{ py: { xs: 10, md: 14 }, bgcolor: '#ffffff', borderTop: '1px solid #ecedf2' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 9, maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2b3154', mb: 1.5 }}>
              Simple, Transparent Plans for Instructors
            </Typography>
            <Typography variant="body1" sx={{ color: '#7a829a' }}>
              Choose the level of automation that fits your driving school business. Students can register and book slots completely free.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {pricingPlans.map((plan, idx) => (
              <Grid size={{ xs: 12, md: 4 }} key={idx}>
                <Card
                  elevation={plan.popular ? 8 : 0}
                  sx={{
                    p: 4.5,
                    height: '100%',
                    borderRadius: '20px',
                    border: plan.popular ? '2px solid #1967D2' : '1px solid #ecedf2',
                    position: 'relative',
                    overflow: 'visible',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: '0px 10px 30px rgba(25, 103, 210, 0.08)'
                    }
                  }}
                >
                  {plan.popular && (
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: -15, 
                        right: 30, 
                        bgcolor: '#1967D2', 
                        color: '#ffffff', 
                        px: 2, 
                        py: 0.5, 
                        borderRadius: '6px', 
                        fontSize: '12px', 
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                      }}
                    >
                      Most Popular
                    </Box>
                  )}

                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2b3154', mb: 1 }}>{plan.title}</Typography>
                    <Typography variant="body2" sx={{ color: '#7a829a', mb: 3.5 }}>{plan.description}</Typography>
                    
                    <Box display="flex" alignItems="baseline" sx={{ mb: plan.note ? 0.5 : 3 }}>
                      <Typography variant="h3" sx={{ fontWeight: '800', color: '#1967D2' }}>{plan.price}</Typography>
                      <Typography variant="subtitle1" sx={{ color: '#7a829a', ml: 1 }}>/{plan.period}</Typography>
                    </Box>
                    {plan.note && (
                      <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 'bold', display: 'block', mb: 3 }}>
                        {plan.note}
                      </Typography>
                    )}

                    <Divider sx={{ my: 3 }} />

                    <List sx={{ mb: 4 }}>
                      {plan.features.map((feature, fIdx) => (
                        <ListItem key={fIdx} disableGutters sx={{ py: 1, alignItems: 'center' }}>
                          <CheckIcon sx={{ color: '#2e7d32', fontSize: 16, mr: 1.5 }} />
                          <ListItemText primary={feature} primaryTypographyProps={{ sx: { fontSize: '14px', color: '#2b3154', fontWeight: '500' } }} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Button
                    fullWidth
                    variant={plan.popular ? 'contained' : 'outlined'}
                    onClick={() => navigate('/register')}
                    sx={{
                      py: 1.8,
                      borderRadius: '12px',
                      fontWeight: 'bold',
                      textTransform: 'none',
                      fontSize: '15px',
                      bgcolor: plan.popular ? '#1967D2' : 'transparent',
                      color: plan.popular ? '#ffffff' : '#1967D2',
                      borderColor: '#1967D2',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: plan.popular ? '#1556b3' : 'rgba(25, 103, 210, 0.05)',
                        borderColor: '#1967D2'
                      }
                    }}
                  >
                    {plan.ctaText}
                  </Button>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Customer Reviews Testimonials (about-section-two bgc-home19-2 style) */}
      {displayTestimonials.length > 0 && currentTestimonial && (
        <Box id="testimonials" sx={{ py: { xs: 10, md: 14 }, bgcolor: '#FEFBF4' }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2b3154', mb: 1.5 }}>
                Customer Reviews
              </Typography>
              <Typography variant="body1" sx={{ color: '#7a829a' }}>
                Read verified testimonials from driving instructors and students.
              </Typography>
            </Box>

            <Box sx={{ maxWidth: 800, mx: 'auto', textAlign: 'center', mb: 5 }}>
              <Rating value={currentTestimonial.rating} readOnly sx={{ color: '#ffc107', mb: 3 }} />
              <Typography 
                variant="h5" 
                sx={{ 
                  fontStyle: 'italic', 
                  color: '#2b3154', 
                  lineHeight: 1.5, 
                  mb: 4.5,
                  fontWeight: 'normal',
                  fontSize: { xs: '1.1rem', md: '1.4rem' } 
                }}
              >
                "{currentTestimonial.comment}"
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1967D2', mb: 0.5 }}>
                {currentTestimonial.name}
              </Typography>
              <Typography variant="caption" sx={{ color: '#7a829a', fontWeight: '500' }}>
                {currentTestimonial.role}
              </Typography>
            </Box>

            {/* Avatar Switcher */}
            <Box display="flex" justifyContent="center" gap={{ xs: 1.5, sm: 3 }} sx={{ mt: 5 }}>
              {displayTestimonials.slice(0, 5).map((testi) => (
                <Box 
                  key={testi.id}
                  onClick={() => setActiveTestiIndex(testi.id)}
                  sx={{ 
                    cursor: 'pointer', 
                    position: 'relative',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'scale(1.1)' }
                  }}
                >
                  <Avatar 
                    src={testi.avatar} 
                    alt={testi.name}
                    sx={{ 
                      width: activeTestiIndex === testi.id ? 64 : 50, 
                      height: activeTestiIndex === testi.id ? 64 : 50, 
                      border: activeTestiIndex === testi.id ? '3px solid #1967D2' : '2px solid transparent',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s'
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Container>
        </Box>
      )}

      {/* Approved Local Test Centres (Featured Companies style) */}
      <Box sx={{ py: { xs: 10, md: 14 }, bgcolor: '#F0F5F7' }}>
        <Container maxWidth="lg">
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyItems="center" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 6 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: '#2b3154' }}>Featured Local Test Centres</Typography>
              <Typography variant="body1" sx={{ color: '#7a829a' }}>We support all major test routes with local qualified instructors.</Typography>
            </Box>
            <Button 
              onClick={() => navigate('/search')}
              endIcon={<ArrowForwardIcon />} 
              sx={{ color: '#1967D2', fontWeight: 'bold', mt: { xs: 2, sm: 0 }, textTransform: 'none', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
            >
              Browse Centres
            </Button>
          </Box>

          <Grid container spacing={3.5}>
            {localTestCentres.map((centre, idx) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={idx}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: '18px',
                    bgcolor: '#ffffff',
                    border: '1px solid #ecedf2',
                    textAlign: 'center',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: '#1967D2',
                      boxShadow: '0px 10px 25px rgba(64, 79, 104, 0.04)',
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <Box sx={{ display: 'inline-flex', p: 1.8, borderRadius: '50%', bgcolor: '#F0F5F7', color: '#1967D2', mb: 2 }}>
                    <BuildingIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2b3154', mb: 1 }}>{centre.name}</Typography>
                  <Typography variant="caption" sx={{ color: '#7a829a', display: 'block', mb: 0.5 }}>{centre.location}</Typography>
                  <Typography variant="caption" sx={{ color: '#1967D2', fontWeight: '600', display: 'block', mb: 2 }}>{centre.instructors}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2b3154', borderTop: '1px solid #ecedf2', pt: 1.5 }}>{centre.rate}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Blog Post / Tips Articles (Blog Post style) */}
      <Box sx={{ py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyItems="center" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 6 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: '#2b3154' }}>Driving School & ADI Tips</Typography>
              <Typography variant="body1" sx={{ color: '#7a829a' }}>Expert guides on driving instructor diary apps, theory exams, and passing your practical driving test.</Typography>
            </Box>
            <Button 
              onClick={() => navigate('/register')}
              endIcon={<ArrowForwardIcon />} 
              sx={{ color: '#1967D2', fontWeight: 'bold', mt: { xs: 2, sm: 0 }, textTransform: 'none', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
            >
              Read All Articles
            </Button>
          </Box>

          <Grid container spacing={4.5}>
            {blogArticles.map((blog, idx) => (
              <Grid size={{ xs: 12, md: 4 }} key={idx}>
                <Card
                  elevation={0}
                  sx={{
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    '&:hover': {
                      '& img': {
                        transform: 'scale(1.04)'
                      },
                      '& .title-link': {
                        color: '#1967D2'
                      }
                    }
                  }}
                >
                  <Box sx={{ overflow: 'hidden', borderRadius: '16px', mb: 2.5, position: 'relative', height: 230 }}>
                    <Box 
                      component="img"
                      src={blog.image}
                      alt={blog.title}
                      sx={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                        transition: 'transform 0.4s ease-in-out'
                      }}
                    />
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: 15, 
                        left: 15, 
                        bgcolor: '#0f162a', 
                        color: '#ffffff', 
                        px: 2, 
                        py: 0.6, 
                        borderRadius: '20px', 
                        fontSize: '12px',
                        fontWeight: 'bold' 
                      }}
                    >
                      {blog.category}
                    </Box>
                  </Box>
                  <CardContent sx={{ p: 0 }}>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
                      <Typography variant="caption" sx={{ color: '#7a829a' }}>{blog.date}</Typography>
                      <Typography variant="caption" sx={{ color: '#ecedf2' }}>|</Typography>
                      <Typography variant="caption" sx={{ color: '#7a829a' }}>{blog.author}</Typography>
                    </Box>
                    <Typography className="title-link" variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2b3154', lineHeight: 1.45, transition: 'color 0.2s' }}>
                      {blog.title}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>



      {/* Main Footer (style-seven style-2 footer style) */}
      <Box sx={{ bgcolor: '#0f162a', color: 'rgba(255,255,255,0.6)', pt: 9, pb: 4.5 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ mb: 7.5 }}>
            {/* Column 1 - Brand info */}
            <Grid size={{ xs: 12, lg: 3.5 }}>
              <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 2.5 }}>
                <CarIcon sx={{ color: '#00FF84', fontSize: 32 }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#ffffff', letterSpacing: '-0.5px', fontFamily: '"Outfit", "Inter", sans-serif' }}>
                  adi<span style={{ color: '#E53935', fontWeight: '900' }}>L</span>essons
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7, fontSize: '14px' }}>
                Automating driving school booking & curriculum operations. Delivering premium WhatsApp notification sync, DVSA trackers, and mock test pass tools.
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontSize: '13px' }}>
                support@adilesson.co.uk
              </Typography>
            </Grid>

            {/* Columns 2-5 Links */}
            <Grid size={{ xs: 12, lg: 8.5 }}>
              <Grid container spacing={4}>
                {/* Column 2 */}
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="subtitle2" sx={{ color: '#ffffff', fontWeight: 'bold', mb: 3 }}>For Students</Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {['Find Instructors', 'Book Lessons', 'DVSA Syllabus Tracker', 'Mock Theory Tests', 'Hazard Perception Videos'].map((item) => (
                      <Typography
                        key={item}
                        onClick={() => navigate('/search')}
                        sx={{ color: 'inherit', textDecoration: 'none', fontSize: '14px', cursor: 'pointer', '&:hover': { color: '#00FF84' } }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Grid>

                {/* Column 3 */}
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="subtitle2" sx={{ color: '#ffffff', fontWeight: 'bold', mb: 3 }}>For Instructors</Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {['Register as ADI', 'Google Calendar Sync', 'HMRC Tax Optimizer', 'Referral Campaigns', 'Test Route Hotspots'].map((item) => (
                      <Typography
                        key={item}
                        onClick={() => navigate('/register')}
                        sx={{ color: 'inherit', textDecoration: 'none', fontSize: '14px', cursor: 'pointer', '&:hover': { color: '#00FF84' } }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Grid>

                {/* Column 4 */}
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="subtitle2" sx={{ color: '#ffffff', fontWeight: 'bold', mb: 3 }}>About Us</Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {['How It Works', 'Pricing Plans', 'Contact Support', 'Privacy Policy', 'Terms of Service'].map((item) => {
                      let path = '/features';
                      if (item === 'Pricing Plans') path = '/pricing';
                      if (item === 'Contact Support') path = '/contact';
                      if (item === 'Privacy Policy') path = '/privacy';
                      if (item === 'Terms of Service') path = '/terms';
                      return (
                        <Typography
                          key={item}
                          onClick={() => navigate(path)}
                          sx={{ color: 'inherit', textDecoration: 'none', fontSize: '14px', cursor: 'pointer', '&:hover': { color: '#00FF84' } }}
                        >
                          {item}
                        </Typography>
                      );
                    })}
                  </Box>
                </Grid>

                {/* Column 5 - App buttons */}
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="subtitle2" sx={{ color: '#ffffff', fontWeight: 'bold', mb: 3 }}>Mobile Apps</Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box 
                      component="img"
                      src="https://creativelayers.net/themes/superio/images/index-21/apple.png"
                      alt="download from app store"
                      sx={{ width: 150, height: 'auto', cursor: 'pointer', filter: 'brightness(0.9)' }}
                    />
                    <Box 
                      component="img"
                      src="https://creativelayers.net/themes/superio/images/index-21/google.png"
                      alt="download from play store"
                      sx={{ width: 150, height: 'auto', cursor: 'pointer', filter: 'brightness(0.9)' }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 4 }} />

          {/* Footer Bottom copyright */}
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyItems="center" justifyContent="space-between" alignItems="center" gap={2}>
            <Typography variant="body2" sx={{ fontSize: '13px' }}>
              © {new Date().getFullYear()} adiLessons. All rights reserved. adiLessons is operated under the brand name adiLessons. Contact: support@adilesson.co.uk.
            </Typography>
            <Box display="flex" gap={3}>
              {['Facebook', 'Twitter', 'Instagram', 'LinkedIn'].map((item) => (
                <Typography
                  key={item}
                  component="a"
                  href="#"
                  sx={{ color: 'inherit', textDecoration: 'none', fontSize: '13px', '&:hover': { color: '#00FF84' } }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
